<template>

  <div v-if="this.form">

    <!-- XOL-552: no lines yet (RTB step 3, or the "+ Customer" popup since round 3) → say so, above the table, so
         "select it below" is literally true. The table (and its header) stays. -->
    <el-alert
      v-if="noLines"
      type="warning"
      show-icon
      :closable="false"
      :title="noLinesMessage" />

    <el-table
    v-if="(this.localNew || this.isRequest) && this.form"
    ref="lines"
    class="backhaul-lines"
    :data="providers"
    border style="width: 100%"
    :role="pickerEditable ? 'radiogroup' : null"
    :aria-label="pickerEditable ? 'Backhaul lines' : null"
    :highlight-current-row="false"
    :row-class-name="lineRowClass"
    max-height="250"
    @current-change="checked">

    <!-- XOL-552: highlight-current-row is OFF on purpose. The only tint is
         is-selected-line, driven from form.backhaul_id (lineRowClass), so an
         existing record shows its line on load and there is one source of
         truth, not two competing blues. current-change still fires without
         it: table-body.js handleClick / table.vue setCurrentRow commit
         setCurrentRow unconditionally and the store emits current-change.
         role="radiogroup" on the table root groups the per-row radios, so a
         screen reader does not announce each one as "1 of 1" — but only while
         the radios exist (pickerEditable). In read mode the root is a plain
         div, where aria-label is ignored, so both attributes are dropped. -->
    <!-- XOL-552 (round 2): the Select column is an edit-mode control. Header
         "Select" and radios while the picker is editable; header BLANK and a
         check mark on the chosen line otherwise (Carlos, 2026-10-01). Kept at
         70px in both modes so nothing moves when Edit is toggled. -->
    <el-table-column label="Select" width="70" align="center"
      class-name="bh-select-col" label-class-name="bh-select-col">
      <template #header>{{ pickerEditable ? 'Select' : '' }}</template>
      <template #default="scope">
        <!-- XOL-552: one-way :value (NO v-model) so checked() stays the only
             writer of form.backhaul_id / provider_id. Wired to @input, NOT
             @change (element-ui radio.vue): a mouse click runs the native
             input's v-model → the `model` setter → $emit('input', label) once
             (handleChange also emits `change`, but only after that, so we do
             not listen to it); a keyboard Space on the focused
             <label role="radio" tabindex="0"> runs `model = label` — the same
             setter, emitting `input` but never `change`. @input is therefore
             the one path that covers both. The setter re-syncs the DOM
             `checked` from the (unchanged) value, so the radio cannot tick
             itself; selectLine → setCurrentRow → current-change → checked() is
             the only route to a write. A radio is only rendered while the
             picker is editable (round 2), so it is never disabled — outside
             edit mode there is no radio to Space on. No @click.native needed:
             the click bubbling to the row hits the same current row, which
             Element ignores as unchanged. The empty span fills the default
             slot so Element does not print the id next to the radio. -->
        <el-radio
          v-if="pickerEditable"
          :value="form.backhaul_id"
          :label="scope.row.id"
          :aria-label="'Select ' + [scope.row.providerShortName, scope.row.backhaulType].filter(Boolean).join(' ') + ' line'"
          @input="selectLine(scope.row)"><span></span></el-radio>
        <!-- Read mode: the chosen line gets the pre-round-1 check mark (its
             glyph is hidden from assistive tech; the text is not). Every other
             row's cell is empty. -->
        <span v-else-if="scope.row.id === form.backhaul_id" class="bh-line-check">
          <i class="fa fa-check" aria-hidden="true"></i><span class="bh-visually-hidden">Selected line</span>
        </span>
      </template>
    </el-table-column>

    <el-table-column prop="backhaulType" label="Type" />
    <el-table-column prop="provider.shortName" label="Provider" />


    <el-table-column prop="backhaul.exclusive" label="Exclusive">
      <template #default="scope">
        <i :class="providers[scope.$index].exclusive ? 'fa fa-check-circle' : 'fa fa-minus-circle'" ></i>
      </template>
    </el-table-column>

    <el-table-column prop="backhaul.depositFee" label="Entrance">
      <template #default="scope">
        {{formatNumber(providers[scope.$index].depositFee)}}
      </template>
    </el-table-column>

    <el-table-column prop="backhaul.rightOfWayCharges" label="ROW">
      <template #default="scope">
        {{formatNumber(providers[scope.$index].rightOfWayCharges)}}
      </template>
    </el-table-column>
  </el-table>

    <!-- XOL-552 (round 2): read mode with lines listed but none marked (a new
         record after Cancel, a Closed / Rejected RTB with no record, a record
         whose line is not on the site). Not in edit mode: Save's own tooltip
         says "Select a backhaul line first" there. -->
    <p v-if="showNoSelectionHint" class="bh-no-selection">
      <i class="fa fa-info-circle" aria-hidden="true"></i> No backhaul line is selected for this customer.
    </p>

    <el-row v-if="!recordPending && !this.form.site_id" :gutter="gutter">
      <el-col :span="24">
        <el-alert type="warning" title="Site Required" description="Please select a site to successfully save this provider." show-icon>
        </el-alert>
        <select-site v-model="form.site_id" no-form :disabled="disabled" />
      </el-col>
    </el-row>

    <!-- XOL-552 (round 2): while this RTB's record is being looked up the pane
         is not mounted at all, so every child captures the loaded values in its
         own data() (SelectInput / SelectBackhaulStatus copy `value` once and
         never watch it). Outside the "+ Backhaul" empty state (noLines already
         replaces the form with a warning). -->
    <div v-if="recordPending && !recordLoadFailed && !noLines"
         v-loading="true"
         element-loading-text="Loading backhaul customer…"
         class="bh-record-loading"
         role="status"
         aria-label="Backhaul customer">
    </div>

    <el-alert v-if="recordLoadFailed && !noLines" type="error" :closable="false" show-icon
              title="Couldn't load backhaul customer">
      <span>This request's backhaul customer record could not be loaded, so it can't be viewed or edited yet.</span>
      <el-button size="small" style="margin-top: 8px;" @click="retryRecordLoad">Retry</el-button>
    </el-alert>

    <!-- XOL-552 (round 2): in request mode the pane is parent-managed —
         BackhaulCustomer owns the ONE edit state (setEditing), so the pane's
         Edit / Cancel / Save buttons and the line picker cannot disagree. The
         drawer and the "+ Customer" popup keep the pane's own state.
         XOL-552 (round 3 follow-up): at step 3 (isRequest) any Tower Co user
         can edit the record, because any Tower Co user who isn't View-Only can
         already CREATE it there (a new record opens editable) and the API
         permits Tower Co add/edit on /backhaul/subscribers with no role
         restriction. The drawer and the "+ Customer" popup stay finance|admin.
         View-Only is still handled by EditPane (Edit disabled, Save hidden,
         and the write is refused in PostMixins).
         XOL-584 (reverts XOL-552 decision A, back to round-2 D8, then loosened
         by Carlos 2026-10-05): on a Closed / Rejected RTB an EXISTING record
         keeps Edit, and a NEW record opens editable as before Feb 2026
         (getBackhaul). Only "+ Backhaul" stays disabled (step3). -->
    <edit-pane
      v-if="!recordPending && form.organisation_id && !noLines"
      ref="bh"
      title="Backhaul Customer"
      :prefix="headerPrefix"
      :editable="isTowerCo() && (isRequest || canAccess('finance|admin')) && !stepFrozen"
      :parent-managed="isRequest"
      :is-edit="editMode"
      :save-disabled="!canSaveCustomer"
      save-disabled-title="Select a backhaul line first"
      @save="onSave"
      @cancel="onCancel"
      @edit="onEdit"
      @add="onSave">

      <div class="padded">
        <!-- :key remounts the atoms when this.form is replaced after mount
             (Cancel's revert): the select atoms copy `value` once. -->
        <el-form :key="formKey" :label-position="this.isDesktop || this.isTablet ? 'top' : 'left'" label-width="134px">

          <el-row :gutter="gutter">

            <el-col :sm="mq.sm" :md="mq.md" :lg="mq.lg">
              <select-backhaul-status v-model="form.status" @change="resetDates" :disabled="disabled && isTowerCo()" />
            </el-col>

            <el-col :sm="mq.sm" :md="mq.md" :lg="mq.lg">
              <switch-input v-model="form.deposit" label="Entrance" :disabled="disabled && isTowerCo()"></switch-input>
            </el-col>

            <el-col :sm="mq.sm" :md="mq.md" :lg="mq.lg">
              <switch-input v-model="form.rightOfWay" label="Right of Way" :disabled="disabled && isTowerCo()"></switch-input>
            </el-col>

            <el-col v-if="localNew" class="bg-primary-lighten-1" style="border-radius: 8px" :sm="mq.sm" :md="mq.md" :lg="mq.lg">
              <date-input v-model="form.startService" label="Serivce Start" placeholder="Started" :disabled="disabled" />
            </el-col>

            <el-col v-if="form.status === 'Terminated'" class="bg-danger-lighten-2" style="border-radius: 8px" :sm="mq.sm" :md="mq.md" :lg="mq.lg">
              <date-input v-model="form.terminateService"
              label="Terminated"
              placeholder="Terminated"
              :disabled="disabled && isTowerCo()" />
            </el-col>

          </el-row>


          <el-collapse v-if="isTowerCo()" v-model="section" accordion>

            <!-- Contact Section -->
            <el-collapse-item name="1">
              <template #title>
                <span class="text-primary"><i><b>Contract</b></i> <span v-if="form.started">: <b>{{formatNumber(form.tenor, 0)}}</b> yr. from <b>{{formatDate(form.started)}}</b> to <b>{{formatDate(form.expiry)}}</b></span></span>
              </template>

              <el-row :gutter="gutter">

                <el-col :sm="24" :md="9">
                  <text-input placeholder="Contract No" v-model="form.contractNo" :disabled="disabled" />
                </el-col>

                <el-col :sm="24" :md="12" :lg="6">
                  <date-input v-model="form.started" placeholder="Started" :disabled="disabled" />
                </el-col>

                <el-col :sm="20" :md="9" :offset="1">
                  <el-form-item placeholder="Tenor">
                    <el-slider
                    v-model="tenor" :min="1" :max="24" :step="1" show-stops :show-input="!localNew"  :disabled="disabled">
                  </el-slider>
                </el-form-item>
              </el-col>


              <el-col :sm="24" :md="12" :lg="6">
                <el-form-item>
                  <b>{{formatFriendlyDate(form.expiry)}}</b> ({{formatDate(form.expiry)}})
                </el-form-item>
              </el-col>
            </el-row>
          </el-collapse-item>



          <!-- Exclusive Section -->
          <el-collapse-item v-if="form.exclusive && canAccess('finance')" name="2">
            <template #title>
              <span class="text-primary"><i><b>Exclusivity</b></i> <span v-if="form.exclusiveExpiry">: expires on <b>{{formatDate(form.exclusiveExpiry)}}</b></span></span>
            </template>

            <el-row :gutter="gutter">

              <el-col :md="6">
                <number-input label="Fee"  v-model="form.exclusiveFee" placeholder="Fee" :disabled="disabled" />
              </el-col>

              <el-col :md="6" :offset="2">
                <date-input v-model="form.exclusiveExpiry" label="Expiry" placeholder="Expiry" :disabled="disabled" />
              </el-col>

              <el-col :md="6" :offset="2">
                <select-frequency  v-model="form.exclusiveFrequency" label="Frequency" placeholder="Frequency" :disabled="disabled" />
              </el-col>

            </el-row>
          </el-collapse-item>


          <!-- Entrance Section -->
          <el-collapse-item  v-if="form.deposit && canAccess('finance')" name="3">
            <template #title>
              <span class="text-primary"><i><b>Deposit / Entrance</b></i> <span v-if="form.depositPaid">: paid on <b>{{formatDate(form.depositPaid)}}</b></span></span>
            </template>
            <el-row :gutter="gutter">
              <el-col :span="6">
                <float-input label="Entrance Fee"  v-model="form.depositFee"  placeholder="Fee" :disabled="disabled" />
              </el-col>

              <el-col :span="12">
                <date-input v-model="form.depositPaid" label="Date Paid" placeholder="Date Paid" :disabled="disabled" />
              </el-col>

              <el-col :span="6">
                <text-input  v-model="form.depositRef" label="Ref" placeholder="Ref" :disabled="disabled" />
              </el-col>
            </el-row>
          </el-collapse-item>


          <!-- Right of Way Section -->
          <el-collapse-item v-if="form.rightOfWay && canAccess('finance')" name="4">
            <template #title>
              <span class="text-primary"><i><b>Right of Way</b></i><!-- <span v-if="form.startService">: from <b>{{formatDate(form.startService)}}</b> to <b>{{formatDate(form.terminateService)}}</b></span> --></span>
            </template>
            <el-row :gutter="gutter">
              <el-col :md="6">
                <float-input v-model="form.rightOfWayCharges" label="Right of Way" placeholder="ROW Charges" :disabled="disabled" />
              </el-col>

              <el-col :md="6" :offset="2">
                <float-input  v-model="form.exclusiveFee" label="Exclusive Fee" placeholder="Exclusive Fee" :disabled="disabled" />
              </el-col>

            </el-row>
          </el-collapse-item>


          <!-- Service Section -->
          <el-collapse-item name="5" v-if="!localNew">
            <template #title>
              <span class="text-primary"><i><b>Serivce</b></i> <span v-if="form.startService">: from <b>{{formatDate(form.startService)}}</b><span v-if="form.terminateService"> to <b class="text-danger">{{formatDate(form.terminateService)}}</b></span></span></span>
            </template>
            <el-row :gutter="gutter">
              <el-col :md="6">
                <date-input v-model="form.startService" label="Serivce Start" placeholder="Started" :disabled="disabled" />
              </el-col>

              <el-col :md="6" :offset="2">
                <date-input v-model="form.terminateService"
                label="Terminated"
                placeholder="Terminated"
                :disabled="disabled || (!editMode && this.form.status == 'Active')" />
              </el-col>
            </el-row>
          </el-collapse-item>


          <!-- Other Fees Section -->
          <el-collapse-item v-if="canAccess('finance')" name="6">
            <template #title>
              <span class="text-primary"><i><b>Sundry &amp; Other Fees</b></i><!-- <span v-if="form.startService">: from <b>{{formatDate(form.startService)}}</b> to <b>{{formatDate(form.terminateService)}}</b></span> --></span>
            </template>

            <el-col :md="6">
              <float-input label="Recurring Fee" v-model="form.recurringFee" placeholder="Sundry Fees" :disabled="disabled" />
            </el-col>

            <el-col :md="6" :offset="2">
              <select-frequency v-model="form.frequency" label="Frequency" :disabled="disabled" />
            </el-col>

          </el-collapse-item>

        </el-collapse>

    </el-form>

  </div>

  <div v-if="form.updated_at" class="pull-right">
    <span class="pull-right">Last update: <b>{{formatFriendlyDate(form.updated_at)}}</b> ({{formatDateTime(form.updated_at)}})<span v-if="form.updated_by"> by {{form.updated_by}}</span></span>
  </div>

</edit-pane>

<div v-else-if="!recordPending && !noLines" class="padded" >
  <el-alert title="Sharer isn't a customer for this provider" show-icon :closable="false" />
</div>

</div>

</template>


<script>


import SiteMixins from '@/mixins/SiteMixins'

import SelectBackhaulType from '@/components/atoms/selects/SelectBackhaulType'
import SelectBackhaulStatus from '@/components/atoms/selects/SelectBackhaulStatus'
import SelectBackhaulProvider from '@/components/atoms/selects/SelectBackhaulProvider'

import SelectSite from '@/components/atoms/selects/SelectSite'

import { pickRequestRecord } from '@/lib/backhaulCustomerRecord'
import { copyText } from '@/lib/requestSyncCopy'


export default {

  mixins: [SiteMixins],

  components: {
    SelectBackhaulType,
    SelectBackhaulStatus,
    SelectBackhaulProvider,
    SelectSite
  },


  props: {

    rowData: {
      type: Object,
      default: () => {
        return {}
      }
    },

    recordId: {
      type: Number,
      default: null
    },

    siteId: {
      type: Number,
      default: null
    },

    sharerId: {
      type: Number,
      default: null
    },

    providerId: {
      type: Number,
      default: null
    },

    actionId: {
      type: Number,
      default: null
    },

    editable: {
      type: Boolean,
      default: false
    },

    isNew: {
      type: Boolean,
      default: false
    },

    isRequest: {
      type: Boolean,
      default: false
    },

    showEditButton: {
      type: Boolean,
      default: true
    },

    tableRef: {
      type: String,
      default: 'backhaultable'
    },

    // Request mode: step 3's workflow readOnly (a Closed / Rejected RTB).
    // XOL-584: no longer read here — Carlos loosened the customer record, so a
    // terminal RTB locks nothing in this component. "+ Backhaul" is locked by
    // step3 passing it to backhaul-action. Kept declared so the prop binding
    // stays valid.
    readOnly: {
      type: Boolean,
      default: false
    },

    // XOL-552: the site's code and name, used only in the "no backhaul has been
    // set up" warning (RTB step 3).
    siteCode: {
      type: String,
      default: null
    },

    siteName: {
      type: String,
      default: null
    },

    // XOL-596 (completed steps): the request step this record is edited from (RTB step 3,
    // party 1): its `action_steps.id`, sent with a save so the API refuses it while the step
    // is complete, and whether the step is complete now (no Edit; a record that does not exist
    // yet opens read-only too). A site page's form passes neither and is unchanged.
    actionStepId: {
      type: Number,
      default: null
    },

    stepFrozen: {
      type: Boolean,
      default: false
    }

  },

  computed: {

    // XOL-552 (round 2): request mode is still looking up THIS RTB's record.
    // The pane, the Site Required prompt and "Sharer isn't a customer" wait for
    // it; the line table renders, locked.
    recordPending () {
      return this.isRequest && !this.recordResolved
    },

    // XOL-552 (round 2): THE predicate for the line picker — radios rendered,
    // rows selectable, selectLine / checked allowed, table a radiogroup. One
    // source, so a locked pane can never leave a live picker behind. Includes
    // `disabled` so the "+ Customer" popup locks after its Save too.
    // XOL-584: no Closed / Rejected clause — records on a terminal RTB can be
    // edited, a NEW one included (getBackhaul opens it editable).
    pickerEditable () {
      return !!(this.editMode && !this.disabled && !this.recordPending)
    },

    // XOL-552 (round 2): read mode, lines listed, none of them the record's —
    // only where there is a picker to explain (step 3, the "+ Customer" popup).
    showNoSelectionHint () {
      return (this.localNew || this.isRequest) &&
        !this.pickerEditable && !this.recordPending &&
        Array.isArray(this.providers) && this.providers.length > 0 && !this.selectedLine
    },

    // XOL-552: the line list has loaded and is EMPTY, in a mount that renders
    // the picker table — RTB step 3 (isRequest) and, since round 3, the "+
    // Customer" popup (localNew), which has its own nested "+ Backhaul". The
    // drawer mount (neither) has no picker and is unchanged. `providers` is null
    // until the fetch resolves — show nothing then, not the warning.
    noLines () {
      return (this.isRequest || this.localNew) && Array.isArray(this.providers) && this.providers.length === 0
    },

    noLinesMessage () {
      var site = [this.siteCode, this.siteName].filter(Boolean).join(' ') || 'this site'

      // A completed step has no "+ Backhaul" (XOL-596), so it must not tell the user to use it.
      if (this.stepFrozen) {
        return copyText('requestSync.completed.noBackhaulLines', { site: site })
      }

      return 'No backhaul has been set up for ' + site +
        '. Use "+ Backhaul" to add one, then select it below.'
    },

    // XOL-552: the listed line the form currently points at, or null. Plain loop
    // (no Array.prototype.find) to match the neighbours.
    selectedLine () {
      var list = this.providers
      var form = this.form

      if (!Array.isArray(list) || !form) return null

      for (var i = 0; i < list.length; i++) {
        if (list[i].id === form.backhaul_id) return list[i]
      }

      return null
    },

    // XOL-552: Save needs a real line with a provider. Gate on the selected ROW,
    // not on form.provider_id alone: in request mode the form can arrive from
    // getBackhaul carrying a provider before the list has loaded a matching row.
    // The drawer edit shows no picker (not new, not a request), so it is never
    // gated — unchanged behaviour.
    canSaveCustomer () {
      if (!(this.localNew || this.isRequest)) return true

      return !!(this.selectedLine && this.selectedLine.provider_id)
    },

    // Header prefix for the EditPane title — names both parties of the backhaul
    // subscription: "<provider> : <customer>" (XOL-264). Both `provider` and
    // `subscriber` are appended by the API on a loaded subscriber record; each
    // part is dropped if absent so the header is never a bare ": Backhaul
    // Customer". EditPane renders "<prefix>: <title>", so the trailing
    // space makes the final separator read " : Backhaul Customer", giving
    // "<provider> : <customer> : Backhaul Customer".
    headerPrefix () {
      var f = this.form
      if (!f) return null
      var provider = f.provider ? (f.provider.shortName || f.provider.name) : null
      var customer = f.subscriber ? (f.subscriber.shortName || f.subscriber.name) : null
      var parts = []
      if (provider) parts.push(provider)
      if (customer) parts.push(customer)
      if (!parts.length) return null
      return parts.join(' : ') + ' '
    }

  },



  methods: {

    // XOL-552 (round 2): the fetch for THIS RTB's record; Retry re-runs it.
    fetchRecord () {
      this.fetchData('backhaul/subscribers/site/' + this.siteId + '/org/' + this.sharerId,
        this.getBackhaul,
        'Backhaul subscriber',
        this.onRecordLoadFailed)
    },

    retryRecordLoad () {
      this.recordLoadFailed = false
      this.fetchRecord()
    },

    // No form and no Save are ever offered on a failed lookup: recordResolved
    // stays false, so the pane stays unmounted.
    onRecordLoadFailed () {
      this.recordLoadFailed = true
    },

    // XOL-552 (round 2): the list is by site + org, so pick THIS RTB's record
    // (src/lib/backhaulCustomerRecord.js) instead of data[0]. A saved record
    // opens read-only; a new one opens editable unless the user is View-Only
    // (no Save to go with it). XOL-584: Closed / Rejected no longer matters —
    // a new record on a terminal RTB opens editable too, as before Feb 2026.
    getBackhaul (data) {

      var record = pickRequestRecord(data, this.actionId)

      if (record) {
        this.localNew = false
        this.form = record
        this.setEditing(false)

      } else {
        this.localNew = true
        this.form = this.getTableStruct('backhaul_subscribers')
        this.form.status = 'Pending'
        this.form.site_id = this.siteId
        this.form.organisation_id = this.sharerId
        this.form.action_id = this.actionId
        // No create on a completed step (XOL-596): the new record opens read-only too.
        this.setEditing(this.canChange() && !this.stepFrozen)
      }

      this.formSnapshot = this.clone(this.form)
      this.recordResolved = true
      this.formKey++

      // A row clicked while the record loaded is remembered by Element; a new
      // record opening editable must not inherit it (see clearTableCurrentRow).
      if (this.editMode && this.lockedRowClicked) this.clearTableCurrentRow()

    },

    getProviders (data) {

      if (!this.checkWarning(data)) {
        this.providers = data

        // XOL-553: only a brand-new record, before a line is picked, starts with no
        // provider. Decide on the form as it is now: in request mode getBackhaul and
        // this callback resolve in either order, so localNew can't be trusted here.
        // A providerId prop also skips the reset, but in request mode the prop is
        // lost anyway: getBackhaul replaces the form without re-applying it (XOL-552).
        var form = this.form
        if (form && !form.id && !form.backhaul_id && !this.providerId) {
          form.provider_id = null
          form.providerShortName = null
        }
      }

    },

    // XOL-552: row highlight is driven from form.backhaul_id, not from the
    // table's current row — so an existing record loaded by getBackhaul is
    // marked on load without checked() firing, in either load order.
    lineRowClass (info) {
      var classes = []

      if (info.row.id === this.form.backhaul_id) classes.push('is-selected-line')
      if (this.pickerEditable) classes.push('is-selectable')

      return classes.join(' ')
    },

    // XOL-552: the radio's route into the ONE writer. Setting the table's
    // current row emits current-change → checked(row).
    selectLine (row) {
      if (!this.pickerEditable) return

      this.$refs.lines.setCurrentRow(row)
    },

    // XOL-552: re-read the site's lines (after "+ Backhaul" saves). Goes
    // through getProviders and its XOL-553 guard, so an existing record and a
    // picked line are left alone and a new form stays unpicked — the new line
    // is NOT auto-selected.
    refreshLines () {
      this.findSiteBackhaulProviders(this.siteId, this.getProviders)
    },

    checked (val) {

      // XOL-552: replacing the table's data can emit current-change(null); that
      // is not a request to clear the selection. Read-only tables select nothing
      // — but Element still remembers a clicked row, so note it (see
      // clearTableCurrentRow).
      if (!val) return

      if (!this.pickerEditable) {
        this.lockedRowClicked = true
        return
      }

      // XOL-552: $set, not plain assignment. The "+ Customer" popup passes no
      // rowData, so the form starts as a bare {} with none of these keys; a plain
      // assignment adds them unobserved and selectedLine / canSaveCustomer /
      // lineRowClass never re-evaluate (Save stays disabled). $set on an existing
      // key is an ordinary reactive write, so request mode is unaffected.
      if (this.form) {
        if (val.provider_id) {
          var provider = {
            shortName: val.providerShortName,
            name: val.providerName
          }

          this.$set(this.form, 'backhaul_id', val.id)
          this.$set(this.form, 'provider_id', val.provider_id)
          this.$set(this.form, 'provider', provider)
          this.$set(this.form, 'backhaul', val)
        } else {
          this.$set(this.form, 'backhaul_id', null)
          this.$set(this.form, 'provider_id', null)
          this.$set(this.form, 'provider', {})
          this.$set(this.form, 'backhaul', {})
        }

      }
    },

    // XOL-552 (round 2): the ONE way edit state changes. `disabled` gates the
    // fields; in request mode the pane is parent-managed, so editMode is the
    // pane's state too. The drawer and popup keep the pane's own state, and
    // editMode there is only what data() gave it.
    setEditing (on) {
      this.disabled = !on

      if (this.isRequest) this.editMode = on
    },

    // Element (table/src/store/current.js) emits current-change only when the
    // current row CHANGES. A click in read mode, or a pick followed by Cancel,
    // leaves its current row set, so clicking that same row after Edit would
    // emit nothing and could not be picked. Clearing emits current-change(null),
    // which checked() ignores.
    clearTableCurrentRow () {
      this.lockedRowClicked = false

      if (this.$refs.lines) this.$refs.lines.setCurrentRow()
    },

    // Request mode: revert to the values the record had when Edit began (or
    // when it loaded), so a cancelled line change is not left showing as the
    // chosen line.
    onCancel () {
      if (this.isRequest && this.formSnapshot) {
        this.form = this.clone(this.formSnapshot)
        this.formKey++
      }

      this.setEditing(false)
      this.clearTableCurrentRow()
    },

    onEdit () {
      if (this.isRequest) this.formSnapshot = this.clone(this.form)

      this.setEditing(true)
      this.clearTableCurrentRow()
    },

    onAdd () {
      this.disabled = false
      this.onSave()
    },

    onSave () {
      this.form.started = this.formatDateSql(this.form.started)
      this.form.expiry = this.formatDateSql(this.form.expiry)

      this.form.exclusiveExpiry = this.formatDateSql(this.form.exclusiveExpiry)
      this.form.depositPaid = this.formatDateSql(this.form.depositPaid)

      this.form.startService = this.formatDateSql(this.form.startService)
      this.form.terminateService = this.formatDateSql(this.form.terminateService)

      if (!this.form.action_id && this.actionId) {
        this.form.action_id = this.actionId
      }

      // The step context rides in the body next to action_id (XOL-596).
      if (this.actionStepId) {
        this.form.actionStepId = this.actionStepId
      }

      // XOL-552 (Q4): in request mode there is no drawer refresh to reload the
      // record, so a second Save would POST a duplicate customer record (seen on
      // RTB 8154). Keep the id the first POST returned so the next Save is a PUT.
      // Request mode also hands over the failure callback and opts into it, so
      // a failed save returns to edit mode instead of looking saved. Success
      // leaves the message on (showMessage true).
      // Leaves edit mode on the click (hides Save: no double POST in flight).
      // Before saveForm: its failure callback can run synchronously (PostMixins'
      // view-only branch), and must be the last word on the edit state.
      this.setEditing(false)

      if (this.isRequest) {
        this.saveForm(this, '/backhaul/subscribers', this.form.id, this.form, this.form.backhaulType, this.onRequestSaved, this.onRequestSaveFailed, true, null, true)
      } else {
        // The "+ Customer" popup (localNew) keeps the new id too, or its next Save
        // would POST a second record.
        this.saveForm(this, '/backhaul/subscribers', this.form.id, this.form, this.form.backhaulType,
          this.localNew ? this.onNewSaved : this.onClose)
      }
/*
      if (this.localNew) {
        this.$emit('save')
      }
*/
    },

    onRequestSaved (data) {
      if (data && data.id) {
        this.$set(this.form, 'id', data.id)
        this.localNew = false
      }

      // XOL-373: "Last update" reflects the save without a refetch.
      this.applyAuditStamp(this.form, data)

      this.onClose()
    },

    // The "+ Customer" popup's first Save: keep the id so the next Save is a PUT.
    // localNew stays true — it drives the picker table and noLines.
    onNewSaved (data) {
      if (data && data.id) {
        this.$set(this.form, 'id', data.id)
      }

      this.applyAuditStamp(this.form, data)

      this.onClose()
    },

    // XOL-552 (round 2): the save failed (the failure is already notified) —
    // back to editing with the edits kept, so it never looks saved.
    onRequestSaveFailed (err) {
      this.setEditing(true)

      // A completed step (XOL-596) refused it: the form stays open with the user's values and
      // the request drawer says why. Any other failure keeps its own toast.
      this.stepCompletedRefusal(err, 'backhaulCustomer', { actionStepId: this.actionStepId })

      // A row clicked while the save was in flight is remembered by Element; clear
      // it so that row can be picked now (see clearTableCurrentRow).
      if (this.lockedRowClicked) this.clearTableCurrentRow()
    },

    onClose () {

      if (this.tableRef && !this.isRequest) {
        this.$events.emit(this.tableRef + '-refresh-table')
      }

    },

    resetDates (value) {
      if (value !== 'Terminated') {
        this.form.terminateService = null
      }
    }

  },


  watch: {

    providerId: function (value) {
      this.editMode = (value !== null)
      if (value !== null) {
        this.form.provider_id = value
      }
    },

    tenor: function (value) {
      if (this.form) {
        this.form.expiry = this.contractExpiryDate(this.form.started, this.tenor, 'years', 'YYYY-MM-DD')
        this.form.tenor = value
      }
    }

  },



  data () {

    return {
      localSiteId: (this.siteId || this.rowData.site_id),
      localNew: this.isNew,
      gutter: 32,
      mqAdd: {sm: 24, md: 24, lg: 8},
      mq: {sm: 24, md: 12, lg: 6},
      section: '1',
      disabled: !this.isNew,
      editMode: (this.editable && this.form.provider_id) || this.isNew,
      form: this.rowData ? this.rowData : this.getTableStruct('backhaul_subscribers'),
      tenor: 1,
      providers: null,

      // XOL-552 (round 2). Request mode waits for its record lookup; every other
      // mount has its record (or none) already.
      recordResolved: !this.isRequest,
      recordLoadFailed: false,
      formKey: 0,
      formSnapshot: null,
      lockedRowClicked: false

    }

  },



  mounted () {

    if (this.isRequest) {
      // getBackhaul decides localNew and the edit state once the record is
      // known; until then the pane is hidden and the picker locked.
      this.setEditing(false)
      this.mq = this.mqAdd

      if (this.siteId && this.sharerId) {
        this.fetchRecord()
      } else {
        this.recordResolved = true
      }
    }

    this.tenor = this.form.tenor ? Number(this.form.tenor) : 1

    this.findSiteBackhaulProviders(this.siteId, this.getProviders)

/*
    if (!this.form.provider_id && !this.form.backhaul_id) {
      this.findSiteBackhaulProviders(this.siteId, this.getProviders)
    }
*/

    if (this.localNew) {
      this.mq = this.mqAdd

      this.form.site_id = this.localSiteId

      if (this.sharerId) {
        this.form.organisation_id = this.sharerId
      } else {
        delete this.form.organisation_id
        delete this.form.organisation
      }

      if (this.providerId) {
        this.form.provider_id = this.providerId
      }

    }

  }
}

</script>

<!-- XOL-552: the selected line is tinted (Element's current-row blue) with a
     left accent, selectable rows show a pointer, and the Select column's
     radio is drawn as a clear input (16px, 2px grey ring; primary on hover /
     checked). Scoped to .bh-select-col so no other el-radio changes. Round 2:
     the radio only exists in edit mode (no disabled styling any more); read
     mode draws a check in the same 26px footprint, so toggling Edit swaps them
     in place. Plain CSS, matching the other components' scoped ::v-deep
     blocks. -->
<style scoped>
.backhaul-lines ::v-deep tr.is-selectable {
  cursor: pointer;
}

.backhaul-lines ::v-deep tr.is-selected-line > td,
.backhaul-lines ::v-deep tr.is-selected-line:hover > td,
.backhaul-lines ::v-deep tr.is-selected-line.hover-row > td,
.backhaul-lines ::v-deep tr.is-selected-line.hover-row.current-row > td {
  background-color: #ecf5ff;
}

.backhaul-lines ::v-deep tr.is-selected-line > td:first-child {
  box-shadow: inset 3px 0 0 #409eff;
}

.backhaul-lines ::v-deep .bh-select-col .el-radio__inner {
  width: 16px;
  height: 16px;
  border: 2px solid #909399;
  background-color: #fff;
}

.backhaul-lines ::v-deep .bh-select-col .el-radio__inner::after {
  width: 6px;
  height: 6px;
}

.backhaul-lines ::v-deep .bh-select-col .el-radio__inner:hover {
  border-color: #409eff;
}

.backhaul-lines ::v-deep .bh-select-col .el-radio__input.is-checked .el-radio__inner {
  border-color: #409eff;
  background-color: #409eff;
}

/* Read-mode check: 16px + 10px margin = the radio's footprint (16px circle +
   the empty label's 10px padding), so the centred box sits where the radio did.
   #3a8ee6 is Element's pressed-primary blue: 3.1:1 on the #ecf5ff tint. */
.backhaul-lines ::v-deep .bh-line-check {
  display: inline-block;
  width: 16px;
  height: 16px;
  line-height: 16px;
  margin-right: 10px;
  vertical-align: middle;
  font-size: 16px;
  color: #3a8ee6;
  cursor: default;
}

/* Local visually-hidden text (the repo has no global .sr-only); same rule as
   Popup.vue's .popup__status. */
.backhaul-lines ::v-deep .bh-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* #606266 is Element's regular text colour (6.2:1 on white). */
.bh-no-selection {
  margin: 8px 0 0;
  font-size: 13px;
  font-style: italic;
  color: #606266;
}

/* Reserves roughly the pane header's height so the page grows once. */
.bh-record-loading {
  min-height: 120px;
  margin-bottom: 1em;
}
</style>
